/**
 * The archive's controlled vocabulary.
 *
 * Modelled on archival practice (EAD "controlled access" headings, LC faceted
 * vocabularies): what a record IS (type / format / provenance) is kept apart
 * from what it is ABOUT (subject / place / names). Every term is a small
 * data entry, so the vocabulary can be reviewed and corrected in one place.
 *
 * How a term is assigned (see noteTags.ts) — each note is scored per term:
 *   +4  the curator used one of the term's hashtags (strongest signal)
 *   +3  a `head` pattern matches the title area (first ~220 characters)
 *   +3  a `url` pattern matches a link in the note
 *   +1  per `match` hit anywhere in the text (capped at 3), and a `match` hit
 *       in the title area adds +3
 * A term is assigned at score >= 3. So one passing mention deep inside a long
 * compilation is NOT enough, but a title mention, a hashtag, or three
 * mentions is.
 */

import { INSCRIPTION_COUNT, ORDISCAN_URL } from '@/lib/siteConfig';

export type Facet = 'type' | 'subject' | 'place' | 'name' | 'format' | 'provenance';

export const FACETS: ReadonlyArray<{ id: Facet; label: string }> = [
  { id: 'type', label: 'Record type' },
  { id: 'subject', label: 'Subject' },
  { id: 'place', label: 'Place' },
  { id: 'name', label: 'People & organizations' },
  { id: 'format', label: 'Format' },
  { id: 'provenance', label: 'Provenance' },
];

export interface TermDef {
  slug: string;
  label: string;
  facet: Facet;
  /** Curator hashtags (lowercase, no #) that assign this term outright. */
  hashtags?: string[];
  /** Patterns counted across the whole text; a title-area hit scores higher. */
  match?: RegExp[];
  /** Patterns tested only against the title area. */
  head?: RegExp[];
  /** Patterns tested against the note's links. */
  url?: RegExp[];
  /** Older slugs that should still resolve to this term (old shared links). */
  aliases?: string[];
}

const MONTHS =
  '(?:January|February|March|April|May|June|July|August|September|October|November|December)';

/** Hosts that are media/inscription infrastructure, not "reference" sources. */
const INFRA_HOSTS =
  '(?:[\\w-]+\\.)*(?:primal\\.net|ord\\.io|mempool\\.space|njump\\.me|nostr\\.build|void\\.cat|nostr\\.band|ordinals\\.com|ordiscan\\.com)';

export const TERMS: ReadonlyArray<TermDef> = [
  // ── Record type: what the record IS ────────────────────────────────────────
  {
    // One term for all of journalism: press reports, broadcasts, and analysis &
    // commentary (op-eds, editorials, essays). Previously three separate terms;
    // their old slugs are kept as aliases so shared links still resolve.
    slug: 'media',
    label: 'Media',
    facet: 'type',
    aliases: ['press', 'broadcast', 'analysis', 'propaganda'],
    match: [
      // Press
      /\bpropaganda\b|\bnewspaper\b|\bheadlines?\b|\bfront page\b/i,
      /\b(?:Jerusalem Post|Washington (?:Post|Times)|New York (?:Times|Post|Daily News)|Reuters|Associated Press|Haaretz|Ha['’]aretz|Times of Israel|Wall Street Journal|Los Angeles Times|Newsweek|The Guardian|BBC|MSNBC|Al Jazeera|USA Today|Boston Globe|Chicago Tribune|London Times|Sunday Times|Daily Telegraph|Yedioth|Ynet|Agence France)\b/i,
      /\bTIME magazine\b|\bSLATE\b|\bPolitico\b|\bThe Atlantic\b/,
      // Journalism
      /\bjournalis(?:m|ts?)\b|\breporters?\b|\bcorrespondents?\b|\bstaff writer\b|\bnews (?:report|story|article)\b/i,
      // Broadcast
      /\b(?:broadcast|footage|newscast|teleprompter|live coverage|WBZ|WABC|WNBC|WCBS)\b/i,
      // Analysis & commentary
      /\b(?:analysis|analytical|commentary|op-ed|editorial|essay|case study)\b/i,
    ],
    head: [/\b(?:CNN|ABC NEWS|NBC NEWS|CBS NEWS|FOX NEWS)\b[^\n]{0,30}\b(?:19|20)\d\d\b/i],
  },
  {
    slug: 'government',
    label: 'Government record',
    facet: 'type',
    aliases: ['document', 'foia'],
    hashtags: ['foia', 'declassified'],
    match: [
      /\b(?:declassified|FOIA|freedom of information|memorandum|intelligence estimate|President['’]s Daily Brief|For the President Only|affidavit|indictment|deposition|court (?:filing|record|order)|commission report|unredacted)\b/i,
      /\bORE \d+\b/,
    ],
    head: [
      new RegExp(
        `\\b(?:CIA|FBI|NSA|State Department|Pentagon|White House|Department of \\w+)\\b[^.\\n]{0,25}\\b${MONTHS}\\b`,
        'i',
      ),
      // A memo header ("To: … 9/12/2001"), but not an email ("To: Ed From: …").
      /\bTo:\s+[A-Z][^\n]{0,60}\d{1,2}\/\d{1,2}\/(?:19|20)\d\d/,
    ],
  },
  {
    slug: 'intercept',
    label: 'Pager intercept',
    facet: 'type',
    hashtags: ['911txts'],
    match: [/\bpager\b/i],
    head: [/\b2001-09-1\d \d\d:\d\d:\d\d\b/, /\b(?:Metrocall|Arch(?:wireless)?|Skytel|Teletouch)\b/i],
  },
  {
    slug: 'interview',
    label: 'Interview or testimony',
    facet: 'type',
    match: [/\b(?:interview(?:ed|s)?|testimony|testif(?:y|ied|ies)|press conference|podcast|keynote|lecture|Q&A)\b/i],
  },
  {
    slug: 'map',
    label: 'Map',
    facet: 'type',
    match: [/\b(?:maps?|cartograph\w*|land ownership|sub-districts)\b/i],
  },
  {
    slug: 'reference',
    label: 'Reference link',
    facet: 'type',
    url: [new RegExp(`https?:\\/\\/(?!${INFRA_HOSTS}\\b)[\\w.-]+\\.[a-z]{2,}`, 'i')],
  },
  {
    slug: 'notice',
    label: 'Archive notice',
    facet: 'type',
    match: [/\b(?:Upscrolled|9\/11 ?receipts|this archive|our archive)\b/i],
  },

  // ── Subject: what the record is ABOUT ──────────────────────────────────────
  {
    slug: 'september-11',
    label: 'September 11 attacks',
    facet: 'subject',
    aliases: ['9-11'],
    hashtags: ['september11', 'sept11', '911', 'worldtradecenter'],
    match: [
      /\b9\/11\b|\b9-11\b|\bSept(?:ember|\.)? 11\b|\bWTC\b|\bWorld Trade Center\b|\bTwin Towers\b|\bGround Zero\b/i,
      /\bSep(?:t)?\.? 1[0-2],? 2001\b/i,
    ],
  },
  {
    slug: 'wtc-collapse',
    label: 'WTC towers & collapse',
    facet: 'subject',
    hashtags: ['wtc', 'worldtradecenter', 'twintowers', 'wtccollapse'],
    match: [
      /\bTwin Towers\b|\b(?:North|South) Tower\b|\bWTC[ -]?[12]\b/i,
      /\b(?:WTC|World Trade Cent(?:er|re))\b[^\n.!?]{0,100}\b(?:collaps\w*|demolition|debris|dust|structural|fires?)\b/i,
      /\b(?:collaps\w*|demolition|debris|dust|structural|fires?)\b[^\n.!?]{0,100}\b(?:WTC|World Trade Cent(?:er|re))\b/i,
    ],
  },
  {
    slug: 'wtc-7',
    label: 'World Trade Center 7',
    facet: 'subject',
    aliases: ['building-7'],
    hashtags: ['wtc7', 'building7'],
    match: [/\bbuilding[ -]7\b|\bWTC[ -]?7\b|\bWorld Trade Center 7\b|\bSalomon Brothers\b/i],
  },
  {
    slug: 'foreknowledge',
    label: 'Foreknowledge & warnings',
    facet: 'subject',
    match: [
      /\bforeknowledge\b|\binsider trading\b|\bput options\b|\badvance (?:warning|knowledge)\b|\bDetermined To Strike\b|\bprior knowledge\b/i,
    ],
  },
  {
    slug: 'hijacked-flights',
    label: 'Hijacked flights',
    facet: 'subject',
    hashtags: ['americanairlines'],
    match: [/\bflight (?:11|77|93|175)\b|\bAmerican Airlines\b|\bUnited Airlines\b|\bhijack\w*\b/i],
  },
  {
    slug: 'dancing-israelis',
    label: 'Dancing Israelis',
    facet: 'subject',
    hashtags: ['dancingisraelis', 'urbanmovingsystems'],
    match: [/\bdancing israelis\b|\bUrban (?:Moving|Removal) Systems\b/i],
  },
  {
    slug: 'zionism',
    label: 'Zionism',
    facet: 'subject',
    hashtags: ['zionism', 'zionist'],
    match: [/\bzionis(?:m|t|ts)\b/i],
  },
  {
    slug: 'terrorism',
    label: 'Terrorism',
    facet: 'subject',
    hashtags: ['waronterror'],
    match: [/\bterroris(?:m|t|ts)\b|\bterror\b/i],
  },
  {
    slug: 'censorship',
    label: 'Censorship & free speech',
    facet: 'subject',
    hashtags: ['censorship', 'freespeech', 'privacy'],
    match: [/\bcensor(?:ship|ed|ing)?\b|\bfree speech\b|\bdeplatform\w*\b/i],
  },
  {
    slug: 'markets',
    label: 'Markets & finance',
    facet: 'subject',
    match: [
      /\bstock(?:s| market)\b|\bWall Street\b|\bNYSE\b|\bNasdaq\b|\bMerrill Lynch\b|\bwallstreetbets\b|\bsecurities\b|\bshort[- ]sell\w*\b|\btrillion\b|\b\$\d+(?:\.\d+)?\s?[TB]\b/i,
    ],
  },
  {
    slug: 'false-flag',
    label: 'False flags & provocations',
    facet: 'subject',
    hashtags: ['falseflag', 'ussliberty'],
    match: [/\bfalse[- ]flag\b|\bOperation Northwoods\b|\bLavon Affair\b|\bGulf of Tonkin\b|\bUSS Liberty\b/i],
  },
  {
    slug: 'uss-liberty',
    label: 'USS Liberty',
    facet: 'subject',
    hashtags: ['ussliberty'],
    match: [/\bUSS Liberty\b/i],
  },
  {
    slug: 'war-policy',
    label: 'War & foreign policy',
    facet: 'subject',
    hashtags: ['waronterror', 'neocons', 'pnac'],
    match: [
      /\bwar on terror\b|\bregime change\b|\bneocons?\b|\bProject for the New American Century\b|\bWesley Clark\b|\bforeign policy\b|\bmilitary[- ]industrial\b/i,
    ],
  },
  {
    slug: 'epstein',
    label: 'Epstein files',
    facet: 'subject',
    aliases: [],
    hashtags: ['epstein', 'epsteinfiles', 'ghislainemaxwell'],
    match: [/\bEpstein\b|\bGhislaine\b|\bMaxwell\b|\bLolita Express\b/i],
  },

  // ── Place ──────────────────────────────────────────────────────────────────
  {
    slug: 'israel',
    label: 'Israel',
    facet: 'place',
    hashtags: ['israel', 'jerusalem'],
    match: [/\bIsrael(?:i|is)?\b|\bTel Aviv\b|\bJerusalem\b(?! Post)/i],
  },
  {
    slug: 'palestine',
    label: 'Palestine',
    facet: 'place',
    aliases: ['gaza'],
    hashtags: ['palestine', 'freepalestine', 'gaza', 'westbank'],
    match: [
      /\bPalestin(?:e|ian|ians)\b|\bGaza\b|\bWest Bank\b|\bRafah\b|\bBethlehem\b|\bHebron\b|\bJenin\b|\bRamallah\b|\bNablus\b|\bNakba\b|\bapartheid\b|\bOccupied Territories\b|\bHamas\b|\bPLO\b/i,
    ],
  },
  {
    slug: 'lebanon',
    label: 'Lebanon',
    facet: 'place',
    hashtags: ['lebanon', 'beirut'],
    match: [/\bLebanon\b|\bBeirut\b|\bHezbollah\b/i],
  },
  {
    slug: 'syria',
    label: 'Syria',
    facet: 'place',
    hashtags: ['syria'],
    match: [/\bSyria(?:n)?\b|\bDamascus\b|\bAssad\b/i],
  },
  {
    slug: 'iraq',
    label: 'Iraq',
    facet: 'place',
    hashtags: ['iraq'],
    match: [/\bIraq(?:i|is)?\b|\bBaghdad\b|\bSaddam\b/i],
  },
  {
    slug: 'iran',
    label: 'Iran',
    facet: 'place',
    hashtags: ['iran'],
    match: [/\bIran(?:ian)?\b|\bTehran\b/i],
  },
  {
    slug: 'afghanistan',
    label: 'Afghanistan',
    facet: 'place',
    hashtags: ['afghanistan'],
    match: [/\bAfghan(?:istan)?\b|\bTaliban\b|\bKabul\b/i],
  },
  {
    slug: 'saudi-arabia',
    label: 'Saudi Arabia',
    facet: 'place',
    match: [/\bSaudi\b|\bRiyadh\b/i],
  },
  {
    slug: 'libya',
    label: 'Libya',
    facet: 'place',
    hashtags: ['libya'],
    match: [/\bLibya(?:n)?\b|\bGadd?afi\b|\bQadd?afi\b|\bTripoli\b/i],
  },
  {
    slug: 'egypt',
    label: 'Egypt',
    facet: 'place',
    hashtags: ['egypt'],
    match: [/\bEgypt(?:ian)?\b|\bCairo\b/i],
  },
  {
    slug: 'russia',
    label: 'Russia',
    facet: 'place',
    match: [/\bRussia(?:n)?\b|\bKremlin\b|\bMoscow\b|\bSoviet\b|\bUSSR\b/i],
  },
  {
    slug: 'jordan',
    label: 'Jordan',
    facet: 'place',
    hashtags: ['jordan'],
    match: [/\bHashemite\b|\bAmman\b|\bKing Abdullah\b|\bJordan(?:ian)? (?:River|Valley|government|army|border)\b/],
  },
  {
    slug: 'yemen',
    label: 'Yemen',
    facet: 'place',
    hashtags: ['yemen'],
    match: [/\bYemen(?:i)?\b|\bHouthi\w*\b/i],
  },
  {
    slug: 'turkey',
    label: 'Turkey',
    facet: 'place',
    hashtags: ['turkey'],
    match: [/\bTurkey\b|\bTurkish\b|\bAnkara\b|\bIstanbul\b/],
  },
  {
    slug: 'pakistan',
    label: 'Pakistan',
    facet: 'place',
    hashtags: ['pakistan'],
    match: [/\bPakistan(?:i)?\b|\bISI\b|\bIslamabad\b/],
  },
  {
    slug: 'washington-dc',
    label: 'Washington, D.C.',
    facet: 'place',
    hashtags: ['washingtondc'],
    match: [/\bWashington,? D\.?C\.?\b|\bCapitol Hill\b/],
  },
  {
    slug: 'new-york',
    label: 'New York City',
    facet: 'place',
    hashtags: ['nyc'],
    match: [/\bNew York(?! (?:Times|Post|Daily))\b|\bNYC\b|\bManhattan\b/i],
  },

  // ── People & organizations ─────────────────────────────────────────────────
  {
    slug: 'mossad',
    label: 'Mossad',
    facet: 'name',
    hashtags: ['mossad'],
    match: [/\bMossad\b/i],
  },
  {
    slug: 'cia',
    label: 'CIA',
    facet: 'name',
    hashtags: ['cia'],
    match: [/\bCIA\b|\bCentral Intelligence\b|\bLangley\b/],
  },
  {
    slug: 'fbi',
    label: 'FBI',
    facet: 'name',
    hashtags: ['fbi'],
    match: [/\bFBI\b|\bFederal Bureau of Investigation\b/],
  },
  {
    slug: 'nsa',
    label: 'NSA',
    facet: 'name',
    hashtags: ['nsa'],
    match: [/\bNSA\b|\bNational Security Agency\b/],
  },
  {
    slug: 'idf',
    label: 'Israel Defense Forces',
    facet: 'name',
    hashtags: ['idf'],
    match: [/\bIDF\b|\bIsrael Defense Forces\b/],
  },
  {
    slug: 'aipac',
    label: 'AIPAC',
    facet: 'name',
    hashtags: ['aipac'],
    match: [/\bAIPAC\b|\bAmerican Israel Public Affairs Committee\b/i],
  },
  {
    slug: 'adl',
    label: 'Anti-Defamation League',
    facet: 'name',
    hashtags: ['adl'],
    match: [/\bADL\b|\bAnti-Defamation League\b/],
  },
  {
    slug: 'wikileaks',
    label: 'WikiLeaks',
    facet: 'name',
    hashtags: ['wikileaks'],
    match: [/\bWikiLeaks\b|\bAssange\b|\bCablegate\b/i],
  },
  {
    slug: 'commission',
    label: '9/11 Commission',
    facet: 'name',
    match: [/\b9\/11 Commission\b|\bZelikow\b|\bShadow Commission\b|\bKean\b/i],
  },
  {
    slug: 'al-qaeda',
    label: 'Al-Qaeda & Osama bin Laden',
    facet: 'name',
    match: [/\bbin Laden\b|\bUsama\b|\bal-?Qaeda\b|\bKhalid Sheikh Mohammed\b/i],
  },
  {
    slug: 'daniel-lewin',
    label: 'Daniel Lewin / Akamai',
    facet: 'name',
    hashtags: ['daniellewin', 'akamai'],
    match: [/\bLewin\b|\bAkamai\b/i],
  },
  {
    slug: 'george-w-bush',
    label: 'George W. Bush',
    facet: 'name',
    match: [/\bGeorge W\.? Bush\b|\bPresident Bush\b|\bPres\. Bush\b/i],
  },
  {
    slug: 'netanyahu',
    label: 'Benjamin Netanyahu',
    facet: 'name',
    hashtags: ['netanyahu'],
    match: [/\bNetanyahu\b/i],
  },
  {
    slug: 'sharon',
    label: 'Ariel Sharon',
    facet: 'name',
    match: [/\bAriel Sharon\b/i],
  },
  {
    slug: 'cryptome',
    label: 'Cryptome',
    facet: 'name',
    hashtags: ['cryptome'],
    match: [/\bCryptome\b|\bJohn (?:L\. )?Young\b/i],
  },
  {
    slug: 'cheney-rumsfeld',
    label: 'Cheney & Rumsfeld',
    facet: 'name',
    match: [/\bCheney\b|\bRumsfeld\b|\bWolfowitz\b|\bPerle\b|\bFeith\b/],
  },
  {
    slug: 'silverstein',
    label: 'Larry Silverstein',
    facet: 'name',
    hashtags: ['silverstein'],
    match: [/\bSilverstein\b/i],
  },
  {
    slug: 'giuliani',
    label: 'Rudy Giuliani',
    facet: 'name',
    match: [/\bGiuliani\b/i],
  },
  {
    slug: 'hamas-plo',
    label: 'Hamas & PLO',
    facet: 'name',
    hashtags: ['hamas'],
    match: [/\bHamas\b|\bPLO\b|\bPalestine Liberation Organization\b|\bIslamic Jihad\b|\bDFLP\b|\bDemocratic Front\b/i],
  },

  // ── Format: derived from attachments, never inferred from wording ──────────
  { slug: 'video', label: 'Video', facet: 'format' },
  { slug: 'image', label: 'Image', facet: 'format' },
  // Slug kept as "pdf" so links shared before the rename still work; it now
  // covers every document file (PDF, Word, text…), hence the broader label.
  { slug: 'pdf', label: 'Doc', facet: 'format', aliases: ['document-file'] },
  { slug: 'text', label: 'Text', facet: 'format' },

  // ── Provenance ─────────────────────────────────────────────────────────────
  {
    slug: 'inscribed',
    label: 'Inscribed on Bitcoin',
    facet: 'provenance',
    aliases: ['bitcoin'],
    hashtags: ['911ordinals', 'ordinals'],
    url: [/\b(?:ord\.io|ordinals\.com|ordiscan\.com|mempool\.space\/tx)\b/i],
  },
];

/**
 * Terms that also count as another term when FILTERING. A record keeps its own
 * tag (its card still says "Government record"); it is simply also found under
 * the broader one. Used so the Document format filter includes government
 * records as well as document files. One-way: Document does not imply
 * Government record.
 */
export const ALSO_FILED_UNDER: Readonly<Record<string, ReadonlyArray<string>>> = {
  government: ['pdf'],
};

/**
 * The Directory's named collections. Each is simply a saved filter over the
 * vocabulary above (any of the listed terms), so its count and its result page
 * are always the same thing.
 */
export interface Inscription {
  id: string;
  title: string;
}

export interface FeaturedLink {
  label: string;
  href: string;
  inscriptionId?: string;
  isManifest?: boolean;
  inscriptions?: ReadonlyArray<Inscription>;
}

export interface Collection {
  id: string;
  title: string;
  caption: string;
  terms: ReadonlyArray<string>;
  /**
   * "main" collections are the archive's principal holdings and are numbered in
   * the Directory; "action" collections are calls to action that happen to be
   * backed by a filter (e.g. Resources).
   */
  kind: 'main' | 'action';
  /**
   * Niche holdings: specialist collections that share one half-width row in the
   * Archive & Directory block instead of each taking a full row.
   */
  niche?: boolean;
  /**
   * When set, the collection is a link out to this address (opened in a new tab)
   * instead of a filter on the archive.
   */
  href?: string;
  /**
   * A fixed count to show for a link-out collection. The archive can't count
   * what lives elsewhere, so this is a number kept by hand (see siteConfig.ts).
   */
  staticCount?: number;
  /** A plain-English paragraph on what the collection is, for its info popup. */
  about?: string;
  /** What kinds of record you will find in it, one short line each. */
  includes?: ReadonlyArray<string>;
  /** Featured external links to display prominently in the collection info popup. */
  featuredLinks?: ReadonlyArray<FeaturedLink>;
}

/** The order here is the order shown in the Directory. */
export const COLLECTIONS: ReadonlyArray<Collection> = [
  {
    id: 'wtc-collapse',
    title: 'WTC Bombing + Demolition',
    caption: 'Twin Towers + WTC 7',
    terms: ['wtc-collapse', 'wtc-7'],
    kind: 'main',
    about:
      'Records concerning the Twin Towers and the collapse of the World Trade Center buildings, including WTC 7: footage, eyewitness accounts, investigations and structural analysis.',
    includes: [
      'North Tower and South Tower records',
      'World Trade Center 7',
      'Collapse footage and eyewitness accounts',
      'Investigations, structural analysis and debris records',
    ],
  },
  {
    id: 'planes',
    title: 'Planes',
    caption: 'Flights 11, 175, 77 + 93',
    terms: ['hijacked-flights'],
    kind: 'main',
    about:
      'Records concerning American Airlines Flights 11 and 77 and United Airlines Flights 175 and 93, including aviation reports, hijacking accounts and flight investigations.',
    includes: [
      'American Airlines Flights 11 and 77',
      'United Airlines Flights 175 and 93',
      'Aviation reports and flight investigations',
      'Hijacking accounts and testimony',
    ],
  },
  {
    id: 'media',
    title: 'Media',
    caption: 'Journalism + Propaganda',
    terms: ['media'],
    kind: 'main',
    about:
      'How the attacks, and the wars that followed, were reported, broadcast and argued over at the time and since.',
    includes: [
      'Newspaper front pages and headlines',
      'Broadcast footage and newscasts',
      'Press reports and journalism',
      'Analysis, commentary, op-eds and essays',
      'Propaganda',
    ],
  },
  {
    id: 'testimony',
    title: 'Testimony',
    caption: 'Interviews + Presentations',
    terms: ['interview'],
    kind: 'main',
    about: 'People speaking for themselves: interviews, testimony and public presentations.',
    includes: [
      'Interviews and podcasts',
      'Testimony',
      'Press conferences',
      'Keynotes, lectures and Q&A',
    ],
  },
  {
    id: 'legal',
    title: 'Legal',
    caption: 'Documents + Evidence',
    terms: ['government'],
    kind: 'main',
    about:
      'Primary documents and evidence: the official record, in the form it was written, for research and for litigation.',
    includes: [
      'Declassified and FOIA releases',
      'Memoranda and intelligence estimates',
      'The President\u2019s Daily Brief',
      'Affidavits, indictments and depositions',
      'Court filings, orders and records',
      'Commission reports',
    ],
  },
  {
    id: 'west-asia',
    title: 'Palestine + West Asia',
    caption: 'History + Genocide',
    terms: [
      'palestine', 'israel', 'lebanon', 'syria', 'iraq', 'iran', 'saudi-arabia', 'egypt', 'libya',
      'jordan', 'yemen', 'turkey',
    ],
    kind: 'main',
    about:
      'The history of the region and the wars that followed, with Palestine at the centre. Any record about one of these places is filed here.',
    includes: [
      'Palestine and Israel',
      'Lebanon, Syria, Iraq and Iran',
      'Saudi Arabia, Egypt and Libya',
      'Jordan, Yemen and Turkey',
    ],
  },
  // The two niche holdings are the last two blocks in the Directory (see `niche`).
  // Pager intercepts moved here from Legal, so the two don't overlap.
   {
     id: 'pager',
     title: 'Pager Intercepts',
     caption: '#911txts',
     terms: ['intercept'],
     kind: 'main',
     niche: true,
     about:
       'WikiLeaks released over 500,000 intercepted US national pager messages from September 11, 2001, covering 24 hours from 3AM to 3AM the following day. Text pagers were typically carried by persons operating in an official capacity, and messages ranged from Pentagon, FBI, FEMA and NYPD exchanges to computer system alerts. 9/11 Receipts has curated and permanently inscribed two thematic releases onto the Bitcoin blockchain: "Media, Market, Israeli" intercepts documenting media reactions and market responses, and "Bomb + Explosion" intercepts capturing real-time reports of the attacks as they unfolded. These curated collections preserve critical historical documentation of the September 11 tragedy in immutable, decentralized form.',
     includes: [
       'Curated intercept releases from the WikiLeaks #911txts archive',
       'Two thematic collections inscribed on Bitcoin Ordinals',
       'Real-time documentation from national pager carriers',
       'Messages from Pentagon, FBI, FEMA, NYPD and financial institutions',
     ],
     featuredLinks: [
       {
         label: 'Media, Market, Israeli — 9/11 Receipts Ordinals #70787182',
         href: 'https://ordiscan.com/inscription/70787182',
         inscriptionId: '70787182',
       },
       {
         label: 'Bomb + Explosion — 9/11 Receipts Ordinals #71715607',
         href: 'https://ordiscan.com/inscription/71715607',
         inscriptionId: '71715607',
       },
       {
         label: 'Original WikiLeaks 9/11 Pager Archive',
         href: 'https://911.wikileaks.org/',
         inscriptionId: undefined,
       },
     ],
   },
  // Bitcoin sends readers to the archive's address on the explorer. `terms` is
  // kept so older shared links (?c=bitcoin) still show the inscribed records.
  {
    id: 'bitcoin',
    title: 'Bitcoin Inscriptions',
    caption: 'On-chain provenance',
    terms: ['inscribed'],
    kind: 'main',
    niche: true,
    href: ORDISCAN_URL,
    staticCount: INSCRIPTION_COUNT,
    about:
      'Records inscribed directly onto the Bitcoin blockchain, where they can never be edited or taken down. The archive\u2019s complete holdings are preserved on the world\u2019s largest distributed ledger, accessible through any Ordinals explorer. Featured inscriptions are listed below, including the master Archive & Directory manifest.',
    includes: [`${INSCRIPTION_COUNT} permanent on-chain inscriptions`, 'Opens in a new tab'],
    featuredLinks: [
      {
        label: '9/11 Receipts Archive & Directory Manifest — Ordinals #105989306',
        href: 'https://ordiscan.com/inscription/105989306',
        inscriptionId: '105989306',
        isManifest: true,
        inscriptions: [
          { id: '70787182', title: 'Wikileaks #911txts "Media, Market, Israeli" pager intercepts' },
          { id: '71715607', title: 'Wikileaks #911txts "Bomb + Explosion" pager intercepts' },
          { id: '72990614', title: 'Mossad ran 9/11 Arab "hijacker" terrorist operation - Wayne Madsen' },
          { id: '98796640', title: 'Anti-Soviet warrior (OBL) puts his army on the road to peace - Robert Fisk [IMAGE]' },
          { id: '98800077', title: 'Anti-Soviet (OBL) warrior puts his army on the road to peace - Robert Fisk' },
          { id: '105926065', title: 'VI: The Specter of Nuclear Terrorism - Benjamin Netanyahu' },
          { id: '74521826', title: 'A Clean Break: A New Strategy for Securing the Realm' },
          { id: '105969578', title: 'Bin Laden Determined To Strike US - For the President Only [DECLASSIFIED]' },
          { id: '73756963', title: 'No Bush-Arafat meeting planned - Kelly Wallace' },
          { id: '70787305', title: 'Arab attacks mark militant upsurge against Israel [IMAGE]' },
          { id: '74522515', title: 'U.S. troops would enforce peace under Army study - Rowan Scarborough' },
          { id: '73154693', title: 'EAST RUTHERFORD POLICE DEPARTMENT - "Dancing Israelis" Police Report' },
          { id: '73226178', title: 'FAA Report: Shooting aboard Flight 11 allegations [FOIA IMAGE]' },
          { id: '76174566', title: 'ATTACK ON AMERICA: Bush vows to retaliate [IMAGE]' },
          { id: '98792912', title: '9/11 Analysis: "Who Is Osama Bin Laden?" - Prof Michel Chossudovsky' },
          { id: '105926314', title: 'Israeli Surveillance of Future Hijackers & FBI Suspects - Gerald Shea' },
          { id: '82053104', title: 'Anthrax Attack Letters' },
          { id: '82250146', title: 'Statement to U.S. Government Reform Committee - Binyamin Netanyahu' },
          { id: '81355434', title: 'Instant Messages To Israel Warned Of WTC Attack - Brian McWilliams' },
          { id: '105918496', title: 'Conspiracy theories say Israel did it. Muslims blame Sharon, Mossad - James Cox' },
          { id: '98800376', title: 'FBI probes advance IM warnings on attacks - George A. Chidi Jr.' },
          { id: '98792911', title: 'Interview with Osama bin Laden. Categorically Denies Involvement in 9/11' },
          { id: '98792910', title: 'When Osama Bin Ladin Was Tim Osman - J. Orlin Grabbe' },
          { id: '81371870', title: '60 Israelis on Tourist Visas Detained Since Sept. 11 - John Mintz' },
          { id: '74563228', title: 'FAA cover up of shooting aboard flight 11 - Paul Sperry' },
          { id: '98800076', title: 'Reports of Israeli Spy Ring Dismissed - John Mintz & Dan Eggen' },
          { id: '98800097', title: 'Spy Rumors Fly on Gusts of Truth - Marc Perelman' },
          { id: '74523380', title: 'Army study suggests U.S. force of 20,000 - The Washington Times' },
          { id: '73745528', title: 'Were Israelis Detained on Sept. 11 Spies? - ABC News' },
          { id: '73302476', title: 'The Strange Story of Daniel Lewin - Shoestring911' },
          { id: '74560038', title: 'ADL: Unraveling Anti-Semitic 9/11 Conspiracy Theories' },
          { id: '81208351', title: 'Design by Israeli ambassador\u2019s son chosen for WTC memorial' },
          { id: '73137828', title: 'MEDIA REACTION ON THIRD ANNIVERSARY OF 9/11 - Wikileaks' },
          { id: '73319682', title: 'Ex-Italian President: Intel Agencies Know 9/11 An Inside Job - Joseph Watson' },
          { id: '81355925', title: 'McCain has been LYING about "911 Terror Attacks!" - Dick Fojut' },
          { id: '73318912', title: 'AL JAZEERA: WE\'LL SHOW ANOTHER 9/11 FILM FOR BALANCE' },
          { id: '82096650', title: 'Fidel Castro: Osama bin Laden is a US agent - PAUL HAVEN' },
          { id: '74560402', title: 'Was Sept.11 a Cover-up of a Financial Fraud? - Gerald Bakes' },
          { id: '74560398', title: 'Al-Jazeera editor remembers 9/11, criticizes US policies, Arab leaders' },
          { id: '81209378', title: 'RE: Red Alert: Osama bin Laden Killed [Letters to STRATFOR] - Wikileaks' },
          { id: '98800377', title: 'The Mysterious Death of Osama bin Laden - Prof Michel Chossudovsky' },
          { id: '105926067', title: '9/11 Revisited: Declassified FBI Files Reveal Details - Greg Fernandez Jr.' },
          { id: '81208219', title: 'British Arrest Score Of Jews As Terrorists; Hotel Bombers Dressed As Arabs [1946]' },
          { id: '81209010', title: 'The Salvador Option in Beirut - Trish Schuh, U.N. OBSERVER' },
          { id: '73745573', title: 'Update: United States vs Khalid Sheik Mohammed et al. - DOD' },
        ],
      },
      {
        label: 'Media, Market, Israeli — 9/11 Receipts Ordinals #70787182',
        href: 'https://ordiscan.com/inscription/70787182',
        inscriptionId: '70787182',
      },
      {
        label: 'Bomb + Explosion — 9/11 Receipts Ordinals #71715607',
        href: 'https://ordiscan.com/inscription/71715607',
        inscriptionId: '71715607',
      },
    ],
  },
  {
    id: 'resources',
    title: 'Resources',
    caption: 'Library + Affiliation',
    terms: ['reference', 'notice'],
    kind: 'action',
    about: 'A library of reference links to outside sources, and notices from the archive about who we are.',
    includes: ['Reference links to outside sources', 'Notices from the archive'],
  },
];
